import { createSlice } from '@reduxjs/toolkit';
import { withDealPricing, dealPricingFields } from '../utils/vendorDiscounts';
import {
    acceptQuotedPrice,
    applyQuote,
    branchProductChanged,
    lineFieldsFor,
    markLinesChanged,
    markLinesForMove,
    markLinesUnchecked,
    oneLocationCart,
    placeCartLine,
    setLineOutlet,
    withoutBranchRead,
} from '../utils/branchCart';

const initialState = {
    currentHealthcareVendor: null,
    carts: {},
    vendor: null,
    shippingAddress: {},
    paymentMethod: null,
    total: 0,
};

const calculateTotal = (carts, vendorId) => {
    const cart = carts[vendorId] || {};
    return Object.values(cart).reduce((total, item) => {
        const optionsTotal = item.options.reduce((sum, option) => sum + option.price, 0);
        return total + (item.price + optionsTotal) * item.quantity;
    }, 0);
};

/* A cart read back from localStorage is checked again at its branch before
   the shopper pays (4 Oct 2026): prices, stock and a branch's sold out may
   all have moved since it was saved. */
const loadCartFromLocalStorage = () => {
    const savedCart = localStorage.getItem('healthcareCart');
    const carts = savedCart ? JSON.parse(savedCart) : {};
    return Object.fromEntries(
        Object.entries(carts || {}).map(([vendorId, cart]) => [
            vendorId,
            markLinesUnchecked(oneLocationCart(cart)),
        ])
    );
};

const loadTotalFromLocalStorage = () => {
    const savedTotal = localStorage.getItem('healthcareTotal');
    return savedTotal ? JSON.parse(savedTotal) : 0;
};

const saveCartToLocalStorage = (carts) => {
    localStorage.setItem('healthcareCart', JSON.stringify(carts));
};

const saveTotalToLocalStorage = (total) => {
    localStorage.setItem('healthcareTotal', JSON.stringify(total));
};

// Persist the current-vendor pointer so a full page reload mid-checkout
// doesn't leave carts populated but currentHealthcareVendor=null.
const loadCurrentHealthcareVendorFromLocalStorage = () => {
    try {
        return localStorage.getItem('healthcareCurrentVendor') || null;
    } catch (e) {
        return null;
    }
};

const saveCurrentHealthcareVendorToLocalStorage = (id) => {
    try {
        if (id) {
            localStorage.setItem('healthcareCurrentVendor', id);
        } else {
            localStorage.removeItem('healthcareCurrentVendor');
        }
    } catch (e) { /* storage unavailable — in-memory state still works */ }
};

const healthcareCartSlice = createSlice({
    name: 'healthcareCart',
    initialState: {
        ...initialState,
        carts: loadCartFromLocalStorage(),
        total: loadTotalFromLocalStorage(),
        currentHealthcareVendor: loadCurrentHealthcareVendorFromLocalStorage(),
    },
    reducers: {
        addToCart: (state, action) => {
            const { vendorId, product, quantity, options = [] } = action.payload;

            if (!state.carts[vendorId]) {
                state.carts[vendorId] = {};
            }
            /* ONE LOCATION PER PHARMACY CART (4 Oct 2026). A pharmacy branch
               runs its own sold out, on/off and price, and the order is
               dispatched from one branch, so a line from another branch moves
               the whole cart there (utils/branchCart placeCartLine; the
               storefront asks first) and every line is checked again. */
            const placed = placeCartLine(state.carts[vendorId], action.payload);
            state.carts[vendorId] = placed.cart;

            // Vendor discounts: `price` on the line is salePrice ?? price, with
            // listPrice and the discount kept beside it.
            const isNewLine = !state.carts[vendorId][product._id];
            if (isNewLine) {
                state.carts[vendorId][product._id] = { ...withDealPricing(withoutBranchRead(product)), quantity: 0, options: [] };
            }

            const productEntry = state.carts[vendorId][product._id];
            Object.assign(productEntry, dealPricingFields(product));
            setLineOutlet(productEntry, placed.outletId);
            // A product read at this very branch confirms the line at the
            // price just copied onto it; anything else is checked before paying.
            const branchFields = lineFieldsFor(product, placed.outletId);
            if (isNewLine || !branchFields.needsCheck) {
                Object.assign(productEntry, branchFields);
            }
            productEntry.quantity += quantity;

            if (quantity > 0) {
                productEntry.options = options;
            }

            state.currentHealthcareVendor = vendorId;
            saveCurrentHealthcareVendorToLocalStorage(vendorId);
            state.total = calculateTotal(state.carts, vendorId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        removeFromCart: (state, action) => {
            const { vendorId, productId } = action.payload;
            if (state.carts[vendorId] && state.carts[vendorId][productId]) {
                delete state.carts[vendorId][productId];
            }
            state.total = calculateTotal(state.carts, vendorId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        setCurrentHealthcareVendor: (state, action) => {
            state.currentHealthcareVendor = action.payload;
            saveCurrentHealthcareVendorToLocalStorage(action.payload);
            // Recalculate total for the new current vendor
            state.total = calculateTotal(state.carts, action.payload);
            saveTotalToLocalStorage(state.total);
        },
        switchHealthcareVendor: (state, action) => {
            state.currentHealthcareVendor = action.payload;
            saveCurrentHealthcareVendorToLocalStorage(action.payload);
            // Recalculate total for the new current vendor
            state.total = calculateTotal(state.carts, action.payload);
            saveTotalToLocalStorage(state.total);
        },
        setHealthcareData: (state, action) => {
            // Stores the pharmacy DOC only. The cart pointer belongs to
            // setCurrentHealthcareVendor, which every surface calls with the id
            // the cart is actually keyed by: the storefront URL id, the same id
            // addToCart writes under, the cart drawer's checkout link carries
            // and the coverage and hours checks use. This reducer used to
            // re-point it to `healthcareId || _id`, the Healthcare document id,
            // which is a different id space from the owner id every directory
            // link carries. That left a returning customer's saved cart
            // unreachable: the badge counted carts[urlId] while the drawer read
            // carts[documentId] and found nothing.
            state.vendor = action.payload;
        },
        saveShippingAddress: (state, action) => {
            state.shippingAddress = action.payload;
        },
        savePaymentMethod: (state, action) => {
            state.paymentMethod = action.payload;
        },
        /* The shopper chose another branch of this pharmacy on the
           storefront (the location picker or "Get it from"). The cart follows
           them and every line is checked there before they pay. */
        moveCartToOutlet: (state, action) => {
            const { vendorId, outletId = null } = action.payload || {};
            const cart = vendorId ? state.carts[vendorId] : null;
            if (!cart || !Object.keys(cart).length) return;
            state.carts[vendorId] = markLinesForMove(cart, outletId);
            saveCartToLocalStorage(state.carts);
        },
        /* The cart check's verdicts (hooks/useBranchCartCheck). */
        applyBranchQuote: (state, action) => {
            const { cartId, verdicts } = action.payload || {};
            const cart = cartId ? state.carts[cartId] : null;
            if (!cart) return;
            const next = applyQuote(cart, verdicts);
            if (next === cart) return;
            state.carts[cartId] = next;
            saveCartToLocalStorage(state.carts);
        },
        /* "Keep at new price" in the review: the line takes the branch price. */
        acceptBranchPrice: (state, action) => {
            const { cartId, productId } = action.payload || {};
            const cart = cartId ? state.carts[cartId] : null;
            if (!cart) return;
            state.carts[cartId] = acceptQuotedPrice(cart, productId);
            state.total = calculateTotal(state.carts, state.currentHealthcareVendor || cartId);
            saveCartToLocalStorage(state.carts);
            saveTotalToLocalStorage(state.total);
        },
        clearCart: (state, action) => {
            const vendorId = action.payload;
            if (state.carts[vendorId]) {
                delete state.carts[vendorId];
                state.total = calculateTotal(state.carts, vendorId);
                saveCartToLocalStorage(state.carts);
                saveTotalToLocalStorage(state.total);
            }
        },
        resetHealthcareCart: (state) => {
            state.currentHealthcareVendor = null;
            saveCurrentHealthcareVendorToLocalStorage(null);
            state.carts = {};
            state.vendor = null;
            state.shippingAddress = {};
            state.paymentMethod = null;
            state.total = 0;
            saveCartToLocalStorage({});
            saveTotalToLocalStorage(0);
        },
    },
    extraReducers: (builder) => {
        /* A branch changed one product live (catalog:public): the lines it
           touches are checked again before Place order. */
        builder.addCase(branchProductChanged, (state, action) => {
            let changed = false;
            Object.keys(state.carts).forEach((vendorId) => {
                const cart = state.carts[vendorId];
                const next = markLinesChanged(cart, action.payload || {});
                if (next !== cart) {
                    state.carts[vendorId] = next;
                    changed = true;
                }
            });
            if (changed) saveCartToLocalStorage(state.carts);
        });
    },
});

export const {
    addToCart,
    removeFromCart,
    setCurrentHealthcareVendor,
    switchHealthcareVendor,
    setHealthcareData,
    saveShippingAddress,
    savePaymentMethod,
    clearCart,
    moveCartToOutlet,
    applyBranchQuote,
    acceptBranchPrice,
    resetHealthcareCart,
    confirmVendorSwitch,
    cancelVendorSwitch,
} = healthcareCartSlice.actions;

export default healthcareCartSlice.reducer;



